'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    tagline: string;
    description: string;
    image: string;
    alt: string;
    icon: string;
    color: string;
    features: string[];
    useCases: string[];
  };
  onDemoClick: (productId: string) => void;
  onLearnMore: (productId: string) => void;
}

export default function ProductCard({ product, onDemoClick, onLearnMore }: ProductCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-border">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={product.image}
          alt={product.alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        <div 
          className="absolute top-4 left-4 w-12 h-12 rounded-lg flex items-center justify-center shadow-lg"
          style={{ backgroundColor: product.color }}
        >
          <Icon name={product.icon as any} size={24} variant="solid" className="text-white" />
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-display font-bold text-2xl text-primary mb-2">{product.name}</h3>
        <p className="font-body text-sm text-accent font-medium mb-3">{product.tagline}</p>
        <p className="font-body text-foreground mb-4 leading-relaxed">{product.description}</p>

        <div className="mb-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-2 text-primary hover:text-accent transition-colors duration-300"
          >
            <span className="font-body font-medium text-sm">
              {isExpanded ? 'Show Less' : 'View Features'}
            </span>
            <Icon 
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
              size={16} 
              variant="solid" 
            />
          </button>

          {isExpanded && (
            <div className="mt-4 space-y-3 animate-slide-in">
              <div>
                <h4 className="font-display font-semibold text-sm text-foreground mb-2">Key Features:</h4>
                <ul className="space-y-1">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                      <span className="font-body text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-display font-semibold text-sm text-foreground mb-2">Use Cases:</h4>
                <div className="flex flex-wrap gap-2">
                  {product.useCases.map((useCase, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-surface text-muted-foreground rounded-full text-xs font-body"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => onDemoClick(product.id)}
            className="flex-1 px-4 py-2.5 bg-conversion text-conversion-foreground rounded-md font-cta text-sm hover:shadow-cta transition-all duration-300"
          >
            Try Demo
          </button>
          <button
            onClick={() => onLearnMore(product.id)}
            className="flex-1 px-4 py-2.5 bg-surface text-foreground rounded-md font-cta text-sm hover:bg-muted transition-all duration-300"
          >
            Learn More
          </button>
        </div>
      </div>
    </div>
  );
}